import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import {
  Accordion,
  ChakraBox,
  coalesceNoteContentToHtml,
  EditorV2State,
  FAIconName,
} from '@equip.health/ui';
import { Formik, FormikProps } from 'formik';
import { DateTime } from 'luxon';
import { Survey as SurveyJS } from 'survey-react-ui';

import { NoteEvents } from '~/analytics';
import ConfirmModal from '~/components/common/ConfirmModal';
import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';
import { EditNoteAccordionAddendumFooter } from '~/components/maud/patients/note/EditNoteAccordionAddendumFooter';
import { EditNoteFooter } from '~/components/maud/patients/note/EditNoteFooter';
import { useGetNoteInitializer } from '~/components/maud/patients/note/useGetNoteInitializer';
import { validateEditNoteFormFieldsFromFormikRef } from '~/components/maud/patients/note/ValidateEditNoteFormFields';
import {
  ACCORDION_HEIGHT_CALCULATED,
  EDIT_NOTE_ACCORDION_WIDTH_LARGE,
} from '~/lib/constants';
import {
  AttachmentType,
  NoteStatus,
} from '~/lib/constants/AddPatientNoteModal.constants';
import { GlobalErrorCode, Listeners } from '~/lib/constants/global.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { isPastDateTime } from '~/lib/util/classes/groupClasses.util';
import Loading from '~/pages/loading';
import {
  useGetGroupClassEventConfirmedAttendeesQuery,
  useGetGroupClassEventDetailsQuery,
  useGetGroupSectionEventDetailsQuery,
  useGetRegistrantsQuery,
  useUpdateGroupClassEventAttendeesMutation,
  useUpdateGroupSectionEventAttendeesMutation,
} from '~/store/services/groupClass';
import {
  mapGroupEventHostsToAttendees,
  splitRegistrantsByDeclineStatus,
} from '~/lib/util/groups.utils';
import {
  useCreateAddendumMutation,
  useUpdateGroupClassNoteMutation,
  useUpdateNoteMutation,
} from '~/store/services/notes/notes';

import EditNoteForm, { FieldErrorState } from './EditNoteForm';
import useAddNote from './useAddNote';
import useAutoSaveNote from './useAutoSaveNote';
import {
  buildCreateAddendumRequest,
  buildUpdateGroupClassNoteRequestFromFormikRef,
  buildUpdateNoteRequestFromRefs,
} from '~/lib/util/notes/noteUpdateRequests.util';
import { buildAddendumAttachmentsPayload } from '~/lib/util/notes/notes.util';
import { useFlags } from 'launchdarkly-react-client-sdk';

export interface EditNoteAccordionProps {
  appointmentDetails: AppointmentDetailResponse;
  dismissAccordion: () => void;
  existingNoteId?: string;
  groupClassEventExternalId?: string;
  groupClassEventTitle?: string;
  inquiryId?: string;
  isAddendumModal?: boolean;
  isEditAttributesEnabled?: boolean;
  isExpanded?: boolean;
  isSessionListEnabled?: boolean;
  patientId: string;
  setIsExpanded?: (expanded: boolean) => void;
}

export type FieldErrorReducer = (
  state: FieldErrorState,
  newState: FieldErrorState,
) => FieldErrorState;

const TOP_LEVEL_ID = 'edit-note';

/**
 * This component is too multi-purpose. Editing patient notes will move
 * to a drawer with its own component focused on that use case.
 * This component will remain for use in:
 *  - Editing group notes
 *  - Adding addenda
 *  In an ideal state there would likely be more focused components for above
 */
const EditNoteAccordion = ({
  appointmentDetails = null,
  dismissAccordion,
  existingNoteId = null,
  groupClassEventExternalId,
  groupClassEventTitle,
  inquiryId,
  isAddendumModal,
  isEditAttributesEnabled,
  isExpanded,
  isSessionListEnabled,
  patientId,
  setIsExpanded,
}: EditNoteAccordionProps) => {
  const { currentUser, listenerCallback } = useGlobalContext();
  const { trackEvent } = useAnalytics();
  const { showErrorToast } = useErrorToast();
  const { setComponentVisibility } = useComponentPortalContext();

  const { groupsRestructuring: isGroupsRestructuringEnabled } = useFlags();

  const [uploadedFiles, setUploadedFiles] = useState<AttachmentType[]>([]);

  const noteTemplateRevisionFormRef = useRef<SurveyJS>(null);
  // String state to store current note id
  const [noteId, setNoteId] = useState<string>(null);

  const [openFinalizeModal, setOpenFinalizeModal] = useState<boolean>(false);

  const [
    openUploadInProgressWarningModal,
    setOpenUploadInProgressWarningModal,
  ] = useState<boolean>(false);

  const [uploadInProgressForSignError, setUploadInProgressForSignError] =
    useState<boolean>(false);

  const isGroupClassNote = !!groupClassEventExternalId;

  const [lastSavedTime, setLastSavedTime] = useState<DateTime>(null);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [hasEventEnded, setHasEventEnded] = useState<boolean>(false);
  const [isEditAttendeesClicked, setIsEditAttendeesClicked] =
    useState<boolean>(false);

  const [selectedAttendees, setSelectedAttendees] = useState<AttendeeItem[]>(
    [],
  );

  const effectiveNoteId = noteId || existingNoteId;

  const [isEditorReady, setIsEditorReady] = useState(false);
  const noteContentEditorRef = useMemo(() => {
    let value: EditorV2State = null;
    return {
      get current() {
        return value;
      },
      set current(v: EditorV2State) {
        value = v;
        setIsEditorReady(v != null);
      },
    } as MutableRefObject<EditorV2State>;
  }, []);

  const initialAttachmentsRef = useRef<AttachmentType[]>([]);

  const {
    data: groupClassEventDetailsData,
    error: getGroupClassEventDetailsError,
  } = useGetGroupClassEventDetailsQuery(groupClassEventExternalId, {
    skip:
      !isGroupClassNote ||
      !groupClassEventExternalId ||
      isGroupsRestructuringEnabled,
  });

  const {
    data: groupDetailsV2,
    error: getGroupSectionEventDetailsError,
    isFetching: isGetGroupSectionEventDetailsFetching,
  } = useGetGroupSectionEventDetailsQuery(groupClassEventExternalId, {
    skip: !groupClassEventExternalId || !isGroupsRestructuringEnabled,
  });

  const {
    data: confirmedAttendeesData,
    isLoading: isGetConfirmedAttendeesLoading,
  } = useGetGroupClassEventConfirmedAttendeesQuery(groupClassEventExternalId, {
    skip:
      !groupClassEventDetailsData ||
      !hasEventEnded ||
      isGroupsRestructuringEnabled,
  });

  const [
    updateGroupClassNote,
    {
      isSuccess: isUpdateGroupClassNoteSuccess,
      isLoading: isUpdateGroupClassNoteLoading,
    },
  ] = useUpdateGroupClassNoteMutation();
  const [
    updateNote,
    { isSuccess: isUpdateNoteSuccess, isLoading: isUpdateNoteLoading },
  ] = useUpdateNoteMutation();
  const [createAddendum] = useCreateAddendumMutation();

  const [updateGroupClassEventAttendees] =
    useUpdateGroupClassEventAttendeesMutation();

  const [updateGroupSectionEventAttendees] =
    useUpdateGroupSectionEventAttendeesMutation();

  const {
    clearAutosaveDebounce,
    debounceFormChange,
    isNoteAutoSaving,
    noteAutoSavingError,
    resetNoteAutoSavingError,
    autoSaveNote,
    autoSaveGroupClassNote,
  } = useAutoSaveNote({ setHasUnsavedChanges, setLastSavedTime });

  // Formik form reference
  const formikRef = useRef<FormikProps<EditNoteFormData>>();

  // Reducer to store mandatory field error
  const [fieldErrorReducer, updateFieldErrorReducer] =
    useReducer<FieldErrorReducer>(
      (state: FieldErrorState, newState: FieldErrorState) => {
        return { ...state, ...newState };
      },
      {
        dateError: null,
        editorError: null,
        titleError: null,
        typeError: null,
        summaryError: null,
        carePlanError: null,
      },
    );

  const {
    data: groupSectionEventRegistrantsData,
    isFetching: isGetRegistrantsFetching,
  } = useGetRegistrantsQuery(groupClassEventExternalId, {
    skip:
      !isGroupClassNote ||
      !groupClassEventExternalId ||
      !isGroupsRestructuringEnabled,
  });

  const resetAllError = () => {
    resetNoteAutoSavingError?.();
    setUploadInProgressForSignError(false);
  };

  const { declinedUsers, registeredUsers } = useMemo(
    () => splitRegistrantsByDeclineStatus(groupSectionEventRegistrantsData),
    [groupSectionEventRegistrantsData],
  );

  const mappedGroupClassEventDetails = useMemo<GroupClassEventDetails>(() => {
    if (!isGroupClassNote) return null;

    if (!isGroupsRestructuringEnabled) {
      return groupClassEventDetailsData;
    }

    if (!groupDetailsV2) {
      return null;
    }

    return {
      attendees: registeredUsers ?? [],
      declinedUsers: declinedUsers ?? [],
      endTime: groupDetailsV2.endTime,
      frequency: groupDetailsV2.frequency as RecurrenceFrequencyValue,
      groupClassEventExternalId: groupClassEventExternalId,
      groupClassExternalId: groupDetailsV2.groupId,
      hosts: mapGroupEventHostsToAttendees(groupDetailsV2.hosts),
      iconName: '',
      startTime: groupDetailsV2.startTime,
      title: groupDetailsV2.title,
    };
  }, [
    declinedUsers,
    groupClassEventDetailsData,
    groupClassEventExternalId,
    groupDetailsV2,
    isGroupClassNote,
    isGroupsRestructuringEnabled,
    registeredUsers,
  ]);

  const attendeesForInitializer = isGroupsRestructuringEnabled
    ? registeredUsers
    : confirmedAttendeesData;

  const confirmedAttendeesForSelection = isGroupsRestructuringEnabled
    ? registeredUsers
    : confirmedAttendeesData;

  const { initialValues, isGetNoteDataLoading, authoredByProviderExternalId } =
    useGetNoteInitializer({
      appointmentDetails,
      confirmedAttendees: attendeesForInitializer,
      declinedUsers,
      groupClassEventDetails: mappedGroupClassEventDetails,
      noteId: effectiveNoteId,
      setLastSavedTime,
      setNoteId,
      setUploadedFiles,
    });

  const htmlInitialNoteContent: string | null = initialValues.noteContentCache
    ? coalesceNoteContentToHtml(initialValues.noteContentCache)
    : null;

  useEffect(() => {
    if (isEditorReady && htmlInitialNoteContent != null && effectiveNoteId) {
      noteContentEditorRef.current
        ?.chain()
        .setContent(htmlInitialNoteContent)
        .run();
    }
  }, [isEditorReady, htmlInitialNoteContent, effectiveNoteId]);

  // useAddNote hook to save note and update status
  const { createNoteError } = useAddNote({
    appointmentExternalId: appointmentDetails?.appointmentExternalId,
    appointmentStartDateTime: appointmentDetails?.appointmentStartDateTime,
    appointmentType: appointmentDetails?.appointmentType,
    existingNoteId,
    groupClassEventExternalId,
    groupClassEventStartTime: mappedGroupClassEventDetails?.startTime,
    groupClassEventTitle,
    inquiryId,
    noteId: effectiveNoteId,
    patientId,
    setNoteId,
  });

  const isAnyMutationLoading =
    isUpdateGroupClassNoteLoading || isUpdateNoteLoading || isNoteAutoSaving;
  const isAnyNoteApiInFlight =
    isAnyMutationLoading ||
    isGetNoteDataLoading ||
    isGetConfirmedAttendeesLoading ||
    isGetGroupSectionEventDetailsFetching ||
    isGetRegistrantsFetching;

  const uploadInProgressFiles = (uploadedFiles ?? []).filter(
    (attachment: AttachmentType) => !attachment.attachmentExternalId,
  );

  // Effect hook called on save note success api call
  useEffect(() => {
    if (isUpdateNoteSuccess || isUpdateGroupClassNoteSuccess) {
      setHasUnsavedChanges(false);
      setComponentVisibility({
        component: PortalComponent.NOTE_ACCORDION,
        hasUnsavedChanges: false,
      });
      if (uploadInProgressFiles.length) {
        setOpenUploadInProgressWarningModal(true);
      } else {
        dismissAccordion();
      }
      // TODO remove this listener when get notes API is moved to rtk query
      listenerCallback([Listeners.APPOINTMENTS_NOTE_REFRESH], [[]]);
    }
  }, [isUpdateNoteSuccess, isUpdateGroupClassNoteSuccess]);

  useEffect(() => {
    const eventDetailsError =
      getGroupClassEventDetailsError || getGroupSectionEventDetailsError;

    if (eventDetailsError) {
      showErrorToast(
        GlobalErrorCode.GET_GROUP_CLASS_EVENT_DETAILS,
        500,
        eventDetailsError.toString(),
      );
    }
  }, [getGroupClassEventDetailsError, getGroupSectionEventDetailsError]);

  useEffect(() => {
    setComponentVisibility({
      component: PortalComponent.NOTE_ACCORDION,
      hasUnsavedChanges,
    });
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!isAddendumModal) return;

    if (
      initialAttachmentsRef.current.length === 0 &&
      uploadedFiles.length > 0
    ) {
      initialAttachmentsRef.current = [...uploadedFiles];
    }
  }, [isAddendumModal, noteId]);

  // Function called during Autosave
  const onAutoSaveDraft = useCallback(() => {
    if (formikRef?.current?.values && effectiveNoteId) {
      if (isGroupClassNote) {
        const groupEventIdentifier = isGroupsRestructuringEnabled
          ? { groupSectionEventExternalId: groupClassEventExternalId }
          : { groupClassEventExternalId };

        autoSaveGroupClassNote(
          buildUpdateGroupClassNoteRequestFromFormikRef({
            formikRef,
            noteId: effectiveNoteId,
            noteContentEditorRef,
            noteStatus: NoteStatus.DRAFT,
            ...groupEventIdentifier,
          }),
        );
      } else {
        const uploadedFilesMetadata = uploadedFiles.map((file) => ({
          attachmentExternalId: file.attachmentExternalId,
          fileName: file.fileName,
          statusCode: file.statusCode,
        }));

        autoSaveNote(
          buildUpdateNoteRequestFromRefs({
            appointmentExternalId: appointmentDetails?.appointmentExternalId,
            authoredByProviderExternalId: currentUser.userId,
            formikRef,
            inquiryId,
            noteId: effectiveNoteId,
            noteStatus: NoteStatus.DRAFT,
            patientId,
            uploadedFiles: uploadedFilesMetadata,
            noteContentEditorRef,
            noteTemplateRevisionFormRef,
          }),
        );
      }
    }
  }, [effectiveNoteId, autoSaveNote, autoSaveGroupClassNote]);

  // Auto Save Debounce method which is passed to useDebounce hook
  const autoSaveDebounce = useCallback(() => {
    if (isGetNoteDataLoading || isAnyMutationLoading || openFinalizeModal)
      return;
    onAutoSaveDraft();
  }, [
    isAnyMutationLoading,
    isGetNoteDataLoading,
    onAutoSaveDraft,
    openFinalizeModal,
  ]);
  /** Method get called whenever form get updated
   * Reason for having this method is, Formik onChange method is not get called automatically, when Custom Field is updated.
   * This onFormChange will be called whenever any Custom Field value get updated.
   * */
  const onFormChange = () => {
    const { titleError, editorError, typeError, dateError } = fieldErrorReducer;
    if (titleError || editorError || typeError || dateError) {
      validateEditNoteFormFieldsFromFormikRef({
        formikRef,
        noteContentEditorRef,
        updateFieldErrorReducer,
      });
    }
    if (!isAddendumModal) {
      debounceFormChange(autoSaveDebounce);
    }
  };

  const onSaveNote = (
    noteStatus: NoteStatus,
    isAddendumModal: boolean = false,
  ) => {
    if (isGroupClassNote) {
      const groupEventIdentifier = isGroupsRestructuringEnabled
        ? { groupSectionEventExternalId: groupClassEventExternalId }
        : { groupClassEventExternalId };

      const body = buildUpdateGroupClassNoteRequestFromFormikRef(
        {
          formikRef,
          noteId: effectiveNoteId,
          noteContentEditorRef,
          noteStatus,
          ...groupEventIdentifier,
        },
        isAddendumModal,
      );
      if (body) {
        updateGroupClassNote({ body }).then(() => {
          if (selectedAttendees.length === 0) {
            return; // request fails if empty array is sent
          }
          if (hasEventEnded) {
            const attendeesPayload = {
              attendees: selectedAttendees,
              groupClassEventExternalId,
              groupSectionEventExternalId: groupClassEventExternalId,
            };
            const attendeesRequest = isGroupsRestructuringEnabled
              ? updateGroupSectionEventAttendees(attendeesPayload)
              : updateGroupClassEventAttendees(attendeesPayload);

            attendeesRequest
              .then(() => {
                if (!isAddendumModal) {
                  setOpenFinalizeModal(false);
                }
              })
              .catch((err) => {
                showErrorToast(
                  GlobalErrorCode.MODIFY_GROUP_CLASS_EVENT_ATTENDEES,
                  500,
                  err?.toString() || 'Unknown error',
                );
              });
          }
        });
      }
    } else {
      const uploadedFilesMetadata = uploadedFiles.map((file) => ({
        attachmentExternalId: file.attachmentExternalId,
        fileName: file.fileName,
        statusCode: file.statusCode,
      }));

      if (isAddendumModal) {
        const attachmentPayload = buildAddendumAttachmentsPayload(
          initialAttachmentsRef.current,
          uploadedFiles,
        );
        const addendaBody = buildCreateAddendumRequest(
          formikRef,
          appointmentDetails?.appointmentExternalId,
          attachmentPayload,
        );
        createAddendum({
          addendaBody,
          noteExternalId: effectiveNoteId,
        }).then((result) => {
          if (!result.error) {
            setHasUnsavedChanges(false);
            setComponentVisibility({
              component: PortalComponent.NOTE_ACCORDION,
              hasUnsavedChanges: false,
            });
            dismissAccordion();
          }
        });
      } else {
        const body = buildUpdateNoteRequestFromRefs({
          appointmentExternalId: appointmentDetails?.appointmentExternalId,
          authoredByProviderExternalId: currentUser.userId,
          formikRef,
          inquiryId,
          noteId: effectiveNoteId,
          noteStatus,
          noteTemplateRevisionFormRef,
          noteContentEditorRef,
          patientId,
          uploadedFiles: uploadedFilesMetadata,
        });
        if (body) {
          updateNote({ body }).then(() => {
            setOpenFinalizeModal(false);
          });
        }
      }
    }
  };

  // Function called on clicking "Keep as Draft"
  const onSaveDraft = () => {
    onSaveNote(NoteStatus.DRAFT);
  };

  // Function called on clicking "Sign & Finalize"
  const onSaveSigned = () => {
    let isValid = true;
    if (noteTemplateRevisionFormRef.current) {
      isValid = noteTemplateRevisionFormRef.current.props.model.validate(
        true,
        true,
      );
    }
    if (isValid) {
      resetAllError();
      const isFieldErrorExist = validateEditNoteFormFieldsFromFormikRef({
        noteContentEditorRef,
        formikRef,
        updateFieldErrorReducer,
      });
      if (isFieldErrorExist) return;

      if (uploadInProgressFiles.length) {
        setUploadInProgressForSignError(true);
        return;
      }
      setOpenFinalizeModal(true);
      clearAutosaveDebounce();
    }
  };

  const onSaveSignedConfirm = () => {
    if (isAddendumModal) {
      resetAllError();
      const isFieldErrorExist = validateEditNoteFormFieldsFromFormikRef({
        formikRef,
        noteContentEditorRef,
        isAddendumModal,
        updateFieldErrorReducer,
      });
      if (isFieldErrorExist) return;
    }
    trackEvent(NoteEvents.finalizeNoteModalFinalizeButtonClicked, {
      Source: 'Note Form',
    });
    onSaveNote(NoteStatus.SIGNED, isAddendumModal);
  };

  const dismissMenu = () => {
    setOpenUploadInProgressWarningModal(false);
    dismissAccordion();
  };

  const handleAttendeeSelection = (userId: string, attended: boolean) => {
    if (hasEventEnded) {
      setSelectedAttendees((prevSelectedAttendees) => {
        return prevSelectedAttendees.map((attendee) => {
          if (attendee.userId === userId) {
            return { ...attendee, attended };
          }
          return attendee;
        });
      });
    }
  };

  useEffect(() => {
    setHasEventEnded(isPastDateTime(mappedGroupClassEventDetails?.endTime));
    let attendees: GroupClassEventAttendee[] = [];
    let declinedUsersToSet: GroupClassEventAttendee[] = [];

    if (confirmedAttendeesForSelection?.length > 0) {
      attendees = confirmedAttendeesForSelection;
    } else if (!hasEventEnded) {
      attendees = mappedGroupClassEventDetails?.attendees;
      declinedUsersToSet = mappedGroupClassEventDetails?.declinedUsers;
    }

    if (hasEventEnded) {
      const allAttendees: AttendeeItem[] =
        attendees?.map((attendee) => ({
          attended: attendee?.attended,
          userId: attendee.externalId,
        })) ?? [];
      const declinedUsersList: AttendeeItem[] =
        declinedUsersToSet?.map((declinedUser) => ({
          attended: false,
          userId: declinedUser.externalId,
        })) ?? [];

      setSelectedAttendees([...allAttendees, ...declinedUsersList]);
    }
  }, [
    confirmedAttendeesForSelection,
    hasEventEnded,
    mappedGroupClassEventDetails,
  ]);

  const isNoteAuthor = authoredByProviderExternalId === currentUser.userId;
  const isGroupClassNoteHost = !!mappedGroupClassEventDetails?.hosts?.find(
    (host) => host.externalId === currentUser.userId,
  );

  const getNotesForm = (
    isAddendumModal: boolean,
    isEditAttributesEnabled: boolean,
  ) => {
    return (
      <ChakraBox>
        <ConfirmModal
          body="Are you sure you want to finalize this note? This action cannot be undone."
          cancelIcon={FAIconName.XmarkRegular}
          cancelLabel="Cancel"
          confirmIcon={FAIconName.checkRegular}
          confirmLabel="Finalize"
          isLoading={isUpdateNoteLoading || isUpdateGroupClassNoteLoading}
          isOpen={openFinalizeModal}
          onClose={() => {
            trackEvent(NoteEvents.finalizeNoteModalCancelButtonClicked, {
              Source: 'Note Form',
            });
            setOpenFinalizeModal(false);
          }}
          onConfirm={onSaveSignedConfirm}
          title="Finalize note"
        />
        <DiscardChangesModalV2
          body="Your attachments are still uploading."
          isOpen={openUploadInProgressWarningModal}
          onClose={() => setOpenUploadInProgressWarningModal(false)}
          onConfirm={dismissMenu}
        />
        <Formik
          enableReinitialize
          initialValues={initialValues}
          innerRef={formikRef}
          onSubmit={onSaveDraft}
        >
          {({ values, setFieldValue }) => (
            <EditNoteForm
              disableField={!noteId}
              fieldErrorReducer={fieldErrorReducer}
              hasEventEnded={hasEventEnded}
              isAddendumModal={isAddendumModal}
              isEditAttendeesClicked={isEditAttendeesClicked}
              isEditAttributesEnabled={isEditAttributesEnabled}
              isGroupClassNote={isGroupClassNote}
              isNoteAuthor={isNoteAuthor}
              isSessionListEnabled={isSessionListEnabled}
              noteContentEditorRef={noteContentEditorRef}
              noteId={noteId}
              noteTemplateRevisionFormRef={noteTemplateRevisionFormRef}
              onAttendeeSelection={handleAttendeeSelection}
              onEditAttendeesClicked={setIsEditAttendeesClicked}
              onFormChange={onFormChange}
              patientId={patientId}
              selectedAttendees={selectedAttendees}
              setFieldValue={(
                key: string,
                value: NoteMultiSelectOption | Array<NoteMultiSelectOption>,
              ) => {
                setFieldValue(key, value);
                setHasUnsavedChanges(true);
              }}
              setUploadedFiles={setUploadedFiles}
              updateFieldErrorReducer={updateFieldErrorReducer}
              uploadedFiles={uploadedFiles}
              values={values}
            />
          )}
        </Formik>
      </ChakraBox>
    );
  };

  return (
    <>
      {isAddendumModal ? (
        <>
          {getNotesForm(isAddendumModal, isEditAttributesEnabled)}
          <EditNoteAccordionAddendumFooter
            dismissAccordion={dismissAccordion}
            isAnyNoteApiInFlight={isAnyNoteApiInFlight}
            isUpdateNoteLoading={isUpdateNoteLoading}
            onSaveSignedConfirm={onSaveSignedConfirm}
          />
        </>
      ) : (
        <Accordion
          dismissMenu={() => {
            if (isExpanded) {
              onSaveDraft();
            } else {
              dismissMenu();
            }
          }}
          footer={
            <EditNoteFooter
              createNoteError={createNoteError}
              dismissAccordion={dismissAccordion}
              existingNoteId={existingNoteId}
              inquiryId={inquiryId}
              isAnyNoteApiInFlight={isAnyNoteApiInFlight}
              isGetNoteDataLoading={isGetNoteDataLoading}
              isGroupClassNoteHost={isGroupClassNoteHost}
              isNoteAuthor={isNoteAuthor}
              isNoteAutoSaving={isNoteAutoSaving}
              isUpdateNoteLoading={isUpdateNoteLoading}
              lastSavedTime={lastSavedTime}
              noteAutoSavingError={noteAutoSavingError}
              noteId={effectiveNoteId}
              onSaveDraft={onSaveDraft}
              onSaveSigned={onSaveSigned}
              uploadInProgressForSignError={uploadInProgressForSignError}
            />
          }
          id={TOP_LEVEL_ID}
          isExpanded={isExpanded}
          panelContentMaxHeight={ACCORDION_HEIGHT_CALCULATED}
          panelMaxWidth={EDIT_NOTE_ACCORDION_WIDTH_LARGE}
          panelMinWidth={EDIT_NOTE_ACCORDION_WIDTH_LARGE}
          setIsExpanded={setIsExpanded}
          title="Edit note"
        >
          <ChakraBox padding="32px">
            {!effectiveNoteId ||
            isGetNoteDataLoading ||
            (isGroupClassNote && !mappedGroupClassEventDetails) ||
            isGetRegistrantsFetching ? (
              <Loading />
            ) : (
              getNotesForm(isAddendumModal, isEditAttributesEnabled)
            )}
          </ChakraBox>
        </Accordion>
      )}
    </>
  );
};

export default EditNoteAccordion;
